import ProductsListPresenter from "./products-list.js";
import CategoriesPresenter from "./categories.js";
import SortingPresenter from "./sorting.js";
import PaginationPresenter from "./pagination.js";

import SectionView from "../view/section.js";
import EmptyView from "../view/empty-section.js";

import Animation from "../../../animation/animation.js";
import ScrollToElement from "../../../scroll-to-element/scroll-to-element.js";
import ChangeUrl from "../../../change-url/presenter/change-url.js";

import {
  createElement,
  removeElement,
  render,
  RenderPosition,
  replace
} from "../../../render/render.js";

import {
  sendXhr
} from "../../../../utils/send-xhr/send-xhr.js";

import {
  isInViewport
} from "../../../../utils/is-in-viewport.js";

import {
  ActionType as FavoriteDataActionType,
  RequestAction,
  CONNECTOR_URL
} from "../../favorite-data/const.js";

import {
  PAGE_KEY,
  LIMIT_KEY,
  SORT_BY_KEY,
  CATEGORY_ID_KEY
} from "../const.js";
export default class FavoriteList {
  constructor(section) {
    this._section = section;

    this._animation = new Animation();
    this._scrollToElement = new ScrollToElement();
    this._changeUrl = new ChangeUrl();

    this._sectionView = null;
    this._emptyView = null;

    this._productsPresenter = null;
    this._paginationPresenter = null;
    this._sortingPresenter = null;
    this._categoriesPresenter = null;

    this._handleFavoriteDataChange = this._handleFavoriteDataChange.bind(this);
    this._handlePaginationChange = this._handlePaginationChange.bind(this);
  }

  init() {
    this._sectionView = new SectionView(this._section);

    if (!this._sectionView.getProductsElement()) {
      return;
    }

    this._productsPresenter = new ProductsListPresenter(
        this._sectionView.getProductsElement()
    );
    this._productsPresenter.init();

    this._categoriesPresenter = new CategoriesPresenter(
        this._sectionView.getCategoriesElement()
    );
    this._categoriesPresenter.init();

    this._sortingPresenter = new SortingPresenter(
        this._sectionView.getSortingListElement()
    );
    this._sortingPresenter.init();

    this._initPagination();

    if (window.FavoriteData) {
      window.FavoriteData.addObserver(this._handleFavoriteDataChange);
    }
  }

  _initPagination() {
    if (this._sectionView.getPaginationElement()) {
      this._paginationPresenter = new PaginationPresenter(
          this._sectionView.getPaginationElement()
      );
      this._paginationPresenter.addObserver(this._handlePaginationChange);
    }
  }

  _updatePagination(paginationMarkup) {
    if (this._paginationPresenter) {
      const paginationWrapper = this._sectionView.getPaginationWrapperElement();
      this._paginationPresenter.removeObserver(this._handlePaginationChange);
      this._paginationPresenter = null;
      removeElement(paginationWrapper);
    }

    if (paginationMarkup) {
      const wrapperElement = createElement(paginationMarkup);
      render(this._sectionView.getProductsElement(), wrapperElement, RenderPosition.AFTEREND);
      this._initPagination();
    }
  }

  _update(data) {
    if (data.isEmpty) {
      this._processClear();
      return;
    }

    this._animation.fadeOut(
        this._sectionView.getProductsElement(),
        null,
        () => {
          this._productsPresenter.destroy();
          const newProductsElement = createElement(data.products);
          replace(newProductsElement, this._sectionView.getProductsElement());
          this._sectionView.updateProductsElement(newProductsElement);
          this._productsPresenter = new ProductsListPresenter(
              this._sectionView.getProductsElement()
          );
          this._productsPresenter.init();

          if (data.categories) {
            removeElement(this._sectionView.getCategoriesElement());
            render(this._sectionView.getActionsWrapper(), createElement(data.categories));
            this._categoriesPresenter = new CategoriesPresenter(
                this._sectionView.getCategoriesElement()
            );
            this._categoriesPresenter.init();
          }
        }
    );

    this._updatePagination(data.pagination);
  }

  _processClear() {
    this._productsPresenter.destroy();
    this._changeUrl.change({}, `${document.location.origin}${document.location.pathname}`);

    [`getProductsElement`, `getPaginationElement`, `getPaginationWrapperElement`, `getCategoriesElement`, `getSortingListElement`, `getActionsWrapper`].forEach((func) => {
      const elementToRemove = this._sectionView[func]();

      if (!elementToRemove) {
        return;
      }

      this._animation.fadeOut(
          elementToRemove,
          null,
          () => {
            removeElement(elementToRemove);

            if (!this._emptyView) {
              this._emptyView = new EmptyView();
              render(this._sectionView.getElement(), this._emptyView.getElement());
            }
          }
      );
    });

    if (!isInViewport(this._sectionView.getHeadingElement())) {
      this._scrollToElement.scroll({
        element: this._sectionView.getHeadingElement()
      });
    }
  }

  _processChangeCategory() {
    this._processUpdate();
  }

  _processSorting() {
    this._processUpdate();
  }

  _processRemoveItem() {
    this._processUpdate();
  }

  _processUpdateData() {
    this._processUpdate();
  }

  _processUpdate(options = {}) {
    const body = [
      `action=${RequestAction.RENDER_FAVORITES}`,
      `type=post`
    ];

    const searchParams = document.location.search.replace(/^\?/i, ``)
      .split(`&`);
    let currentStateParams = {};

    // Preparing the current state params.
    [CATEGORY_ID_KEY, PAGE_KEY, LIMIT_KEY, SORT_BY_KEY].forEach((key) => {
      const param = searchParams
        .filter((item) => {
          item = item.split(`=`);
          return item[0] === key;
        })
        .map((item) => {
          return item.split(`=`);
        })
        .flat();

      if (param.length > 0) {
        currentStateParams[param[0]] = param[1];
      }
    });

    if (this._sortingPresenter.getActiveSorting()) {
      currentStateParams[SORT_BY_KEY] = this._sortingPresenter.getActiveSorting();
    } else {
      delete currentStateParams[SORT_BY_KEY];
    }

    if (this._categoriesPresenter.getActiveCategoryId()) {
      currentStateParams[CATEGORY_ID_KEY] = this._categoriesPresenter.getActiveCategoryId();
    } else {
      delete currentStateParams[CATEGORY_ID_KEY];
    }

    // Setting the page property.
    if (options.page) {
      currentStateParams[PAGE_KEY] = options.page;
    } else {
      delete currentStateParams[PAGE_KEY];
    }

    // Setting the body data.
    Object.entries(currentStateParams)
      .forEach(([key, value]) => {
        body.push(`${key}=${value}`);
      });

    // Preparing the current state properties
    currentStateParams = Object
      .entries(currentStateParams)
      .map((item) => {
        return `${item[0]}=${item[1]}`;
      });

    // Preparing the URL.
    let uri = (currentStateParams.length > 0) ?
      `${document.location.pathname}?${currentStateParams.join(`&`)}` :
      `${document.location.pathname}`;
    body.push(`uri=${JSON.stringify(uri).split(`&`)}`);

    const requestArgs = {
      body,
      connectorUrl: CONNECTOR_URL,
      loadCallback: (response) => {
        const data = JSON.parse(response);

        if (data.reset) {
          currentStateParams = currentStateParams.filter((item) => {
            item = item.split(`=`);
            return item[0] !== CATEGORY_ID_KEY;
          });

          uri = (currentStateParams.length > 0) ?
            `${document.location.pathname}?${currentStateParams.join(`&`)}` :
            `${document.location.pathname}`;

        }

        this._changeUrl.change(currentStateParams, `${document.location.origin}${uri}`);
        this._update(data);
      },
    };

    sendXhr(requestArgs);
  }

  _handleFavoriteDataChange(action) {
    switch (action) {
      case FavoriteDataActionType.CLEAR:
        this._processClear();
        break;
      case FavoriteDataActionType.CHANGE_CATEGORY:
        this._processChangeCategory();
        break;
      case FavoriteDataActionType.SORT:
        this._processSorting();
        break;
      case FavoriteDataActionType.REMOVE_PRODUCT:
        this._processRemoveItem();
        break;
      case FavoriteDataActionType.UPDATE_DATA:
        this._processUpdateData();
        break;
    }
  }

  _handlePaginationChange(action, payload) {
    this._processUpdate({
      page: payload.page
    });
  }
}
